import { currentIdToken, signOut } from './auth'
import { env } from './env'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

export type ApiResult<T> = { data: T; meta: Record<string, unknown> }

type Envelope<T> = {
  success: boolean
  data: T
  meta?: Record<string, unknown>
  error: { code: string; message: string } | null
}

// Quem chama decide o que fazer na sessão expirada; o padrão é voltar ao login.
let onUnauthorized: () => void = () => undefined
export const setUnauthorizedHandler = (handler: () => void): void => {
  onUnauthorized = handler
}

export function buildUrl(path: string, query: Record<string, string | number | undefined | null> = {}): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  }
  const qs = params.toString()
  return `${env.apiUrl}${path}${qs ? `?${qs}` : ''}`
}

export type RequestOptions = { method?: 'GET' | 'POST' | 'DELETE'; body?: unknown }

export async function api<T>(
  path: string,
  query: Record<string, string | number | undefined | null> = {},
  options: RequestOptions = {},
): Promise<ApiResult<T>> {
  const token = await currentIdToken()
  if (!token) {
    onUnauthorized()
    throw new ApiError(401, 'UNAUTHORIZED', 'Sessão expirada')
  }

  let response: Response
  try {
    const method = options.method ?? 'GET'
    response = await fetch(
      buildUrl(path, query),
      method === 'GET'
        ? { headers: { Authorization: token } }
        : {
            method,
            headers: { Authorization: token, 'Content-Type': 'application/json' },
            body: options.body === undefined ? undefined : JSON.stringify(options.body),
          },
    )
  } catch {
    throw new ApiError(0, 'NETWORK', 'Sem conexão com a API do backoffice')
  }

  if (response.status === 401) {
    signOut()
    onUnauthorized()
    throw new ApiError(401, 'UNAUTHORIZED', 'Sessão expirada')
  }

  let body: Envelope<T>
  try {
    body = (await response.json()) as Envelope<T>
  } catch {
    throw new ApiError(response.status, 'INVALID_RESPONSE', `Resposta inesperada da API (${response.status})`)
  }
  if (!response.ok || !body.success) {
    throw new ApiError(response.status, body.error?.code ?? 'ERROR', body.error?.message ?? `Erro ${response.status}`)
  }
  return { data: body.data, meta: body.meta ?? {} }
}
